import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories, features, testimonials, faqs } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function HeroSection() {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      const found = comics.find(
        (c) => c.title.includes(searchTerm.trim()) || c.author.includes(searchTerm.trim())
      );
      if (found && typeof window !== 'undefined') {
        window.location.href = `/comic/${found.slug}`;
      }
    }
  };

  return (
    <section className="relative w-full h-[500px] md:h-[650px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20illustration%20of%20colorful%20manga%20and%20comic%20panels%20floating%20in%20a%20dreamy%20abstract%20space%2C%20warm%20orange%20and%20coral%20gradients%2C%20soft%20bokeh%20effects%2C%20Japanese%20and%20Korean%20comic%20style%20elements%20blending%20together%2C%20whimsical%20and%20inviting%20atmosphere%2C%20no%20text%2C%20digital%20art%2C%20cinematic%20composition%2C%20warm%20lighting&width=1600&height=900&seq=hero-bg-comics-01&orientation=landscape"
        alt="汗汗漫画 - 免费漫画大全"
        title="汗汗漫画网 - 韩漫日漫国漫免费漫画"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 leading-tight">
            汗汗漫画
          </h1>
          <p className="text-base md:text-lg text-background-200/90 mb-3 max-w-xl mx-auto">
            韩漫日漫国漫免费漫画全集分类
          </p>
          <p className="text-sm md:text-base text-background-300/80 mb-8 max-w-lg mx-auto">
            下拉式连续阅读漫画大全 | 在线看漫画流畅阅读体验
          </p>

          <form onSubmit={handleSearch} className="max-w-md mx-auto flex gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索漫画名称或作者..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-background-50/95 text-foreground-900 text-sm placeholder:text-foreground-400 border border-background-200/50 focus:outline-none focus:ring-2 focus:ring-primary-400/50 transition-all"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="flex items-center justify-center gap-6 mt-8">
            <Link
              to="/category/hanman"
              className="text-xs md:text-sm text-background-200 hover:text-background-50 transition-colors whitespace-nowrap"
            >
              韩漫
            </Link>
            <Link
              to="/category/riman"
              className="text-xs md:text-sm text-background-200 hover:text-background-50 transition-colors whitespace-nowrap"
            >
              日漫
            </Link>
            <Link
              to="/category/guoman"
              className="text-xs md:text-sm text-background-200 hover:text-background-50 transition-colors whitespace-nowrap"
            >
              国漫
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="px-4 md:px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '3000+', label: '漫画作品' },
            { value: '500万+', label: '活跃读者' },
            { value: '10+', label: '漫画分类' },
            { value: '每日更新', label: '更新频率' },
          ].map((stat, i) => (
            <div key={i}>
              <div className="font-heading text-2xl md:text-3xl text-primary-600">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
            漫画分类导航
          </h2>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            按类型浏览，快速找到你喜欢的漫画
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <div className="text-center">
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="block text-xs text-foreground-400 mt-0.5">
                  {cat.count}部
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotComicsSection({ comicsList }: { comicsList: Comic[] }) {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              热门漫画推荐
            </h2>
            <p className="text-sm text-foreground-500">最受读者欢迎的漫画作品</p>
          </div>
          <Link
            to="/category/hanman"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {comicsList.slice(0, 6).map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestComicsSection({ comicsList }: { comicsList: Comic[] }) {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500">刚刚更新的漫画章节</p>
          </div>
          <Link
            to="/category/riman"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {comicsList.slice(6, 12).map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
            为什么选择汗汗漫画？
          </h2>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            我们致力于提供最好的在线漫画阅读体验
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <div
              key={i}
              className="flex gap-4 p-5 rounded-xl bg-background-100 hover:bg-background-200 transition-colors"
            >
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${feature.icon} text-xl`}></i>
              </div>
              <div>
                <h3 className="text-base font-medium text-foreground-900 mb-1">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
            读者口碑
          </h2>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            来自真实读者的评价与反馈
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="p-5 rounded-xl bg-background-50"
              onMouseEnter={() => setActiveIndex(i)}
            >
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, starIdx) => (
                  <i
                    key={starIdx}
                    className={`ri-star-fill text-sm ${
                      starIdx < t.rating
                        ? 'text-primary-500'
                        : 'text-foreground-200'
                    }`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                &ldquo;{t.comment}&rdquo;
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-medium">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="text-xs font-medium text-foreground-800">{t.name}</div>
                  <div className="text-xs text-foreground-400">{t.date}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500">关于汗汗漫画的常见疑问解答</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="rounded-xl bg-background-100 overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-800 pr-4">
                  {faq.question}
                </span>
                {openIndex === i ? (
                  <i className="ri-arrow-up-s-line text-foreground-400 flex-shrink-0"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-400 flex-shrink-0"></i>
                )}
              </button>
              {openIndex === i && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9omtjgsenbnioleshu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '订阅失败';
        if (serverMsg.includes('spam')) {
          setErrorMsg('订阅失败，请稍后再试');
        } else {
          setErrorMsg(serverMsg);
        }
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请稍后再试');
      setStatus('error');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-primary-500">
      <div className="px-4 md:px-6 max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-3">
          订阅更新通知
        </h2>
        <p className="text-sm text-background-100/80 mb-6">
          第一时间获取最新漫画更新和热门推荐
        </p>
        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="anti-spam-field"
          />
          <input
            type="email"
            name="email"
            placeholder="请输入您的邮箱地址"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 px-4 py-3 rounded-full bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-300"
          />
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="px-6 py-3 rounded-full bg-background-950 text-background-50 font-medium text-sm hover:bg-foreground-900 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
          >
            {status === 'submitting' ? '订阅中...' : '立即订阅'}
          </button>
        </form>
        {status === 'success' && (
          <p className="mt-3 text-sm text-background-100">订阅成功！感谢您的关注。</p>
        )}
        {status === 'error' && (
          <p className="mt-3 text-sm text-background-100">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const hotComics = [...comics].sort((a, b) => b.views - a.views);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <HeroSection />
        <StatsSection />
        <CategorySection />
        <HotComicsSection comicsList={hotComics} />
        <LatestComicsSection comicsList={latestComics} />
        <FeaturesSection />
        <TestimonialsSection />
        <FAQSection />
        <NewsletterSection />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '汗汗漫画网',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '汗汗漫画网提供韩漫、日漫、国漫等主流漫画类型分类，汇集免费漫画全集、下拉式连续阅读模式，在线看漫画流畅不卡顿，尽享热门漫画大全。',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/category/hanman?q={search_term_string}`,
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '汗汗漫画网',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '免费在线漫画阅读平台 - 韩漫日漫国漫免费漫画全集',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@hanhancomic.com',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}